import { useCallback, useEffect, useRef, useState } from "react";
import { useWebSocketContext } from "../contexts/WebSocketProvider";
import { useParams } from "react-router-dom";

// Utility: get default last 30 days range
function getDefaultRange() {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 30);

  return {
    fromDate: start.toISOString().split("T")[0],
    toDate: end.toISOString().split("T")[0],
  };
}

export default function useTotalEnergyByStation(range = null) {
  const [totalEnergy, setTotalEnergy] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { send, isConnected, registerHandler } = useWebSocketContext();
  const mountedRef = useRef(true);
  const { stationId } = useParams();

  const fetchTotalEnergy = useCallback(async () => {
    if (!mountedRef.current) return;
    if (!isConnected) {
      setError("WebSocket not connected");
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const { fromDate, toDate } = range || getDefaultRange();

      const payload = {
        station_id: stationId?.toString(),
        start_date: fromDate,
        end_date: toDate,
      };

      console.log("Payload being sent (station energy):", payload);

      const response = await send("cms_get_total_energy_consumed_by_station_id", payload);

      if (!response || response.type !== "cms_get_total_energy_consumed_by_station_id_response") {
        throw new Error("Invalid response from server");
      }

      if (typeof response.data !== "number") {
        throw new Error("Server did not return a valid energy value");
      }

      setTotalEnergy(response.data);
    } catch (err) {
      console.error("Error fetching total energy (station):", err);
      setError(err.message);
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, [isConnected, send, stationId, range]);

  // Live updates subscription
  useEffect(() => {
    const unregister = registerHandler("total_energy_update_station", (msg) => {
      if (typeof msg.data === "number" && mountedRef.current) {
        setTotalEnergy(msg.data);
      }
    });
    return () => unregister();
  }, [registerHandler]);

  // Initial fetch
  useEffect(() => {
    mountedRef.current = true;
    fetchTotalEnergy();
    return () => {
      mountedRef.current = false;
    };
  }, [fetchTotalEnergy]);

  return { totalEnergy, isLoading, error, refetch: fetchTotalEnergy };
}
